import { Component, OnInit, inject } from '@angular/core';
import { Background } from '../../shared/components/background/background';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { Connect } from '../../shared/components/connect/connect';
import { Button } from '../../shared/components/button/button';
import { LeadSection } from '../landing/components/lead-section/lead-section';
import { TagServices } from '../landing/components/tag-services/tag-services';
import { StepsSection } from '../landing/components/steps-section/steps-section';
import { IconSection } from '../landing/components/icon-section/icon-section';
import { DevServices } from '../landing/components/dev-services/dev-services';
import { RevealDirective } from '../../shared/directives/reveal.directive';
import { CalendlyService } from '../../shared/services/calendly.service';
import { SeoService } from '../../shared/services/seo.service';
import {
  AEO_SECTIONS_INTRO,
  AEO_SECTIONS_TECH,
  AEO_SECTIONS_GROWTH,
} from '../../shared/data/aeo-sections';

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What is Answer Engine Optimization (AEO)?',
    a: 'AEO is the process of increasing your brand’s visibility within AI-powered answer engines such as ChatGPT, Google AI Overviews, Perplexity, Claude, and Gemini. Unlike traditional SEO, which ranks web pages, AEO focuses on becoming the answer that AI systems recommend when users ask questions.',
  },
  {
    q: 'How is AEO different from traditional SEO?',
    a: 'Traditional SEO optimizes pages to rank in search results and measures success by rankings. AEO optimizes for large language models, retrieval systems, and answer engines, and measures recommendations, citations, visibility, and share of voice across AI platforms.',
  },
  {
    q: 'How do you get a brand cited and recommended by ChatGPT and other AI tools?',
    a: 'We strengthen authority signals, entity recognition, structured content, and third-party validation across the web — and build trusted citations across industry publications, news sites, review platforms, directories, podcasts, and forums — so AI systems recognize your company as a trusted answer.',
  },
  {
    q: 'What is entity optimization and why does it matter for AI search?',
    a: 'Modern AI systems think in entities, not keywords. Entity optimization — knowledge graph development, entity mapping, brand disambiguation, and relationship building — helps AI systems understand that your brand exists, what it does, and how it relates to other entities before recommending it.',
  },
  {
    q: 'What is Generative Engine Optimization (GEO)?',
    a: 'GEO focuses on how generative AI systems retrieve, evaluate, and synthesize information across the entire AI ecosystem — ChatGPT, Perplexity, Claude, Gemini, Google AI, and future platforms. It shares fundamentals with AEO but targets broader AI visibility.',
  },
  {
    q: 'How do you measure AEO results?',
    a: 'Our reporting framework tracks AI citations, brand mentions, competitive visibility, answer engine presence, share of voice, entity growth, and authority development, so you can monitor how AI systems perceive and reference your brand over time.',
  },
];

@Component({
  selector: 'app-aeo-marketing-service',
  imports: [Background, HeroSection, Connect, Button, LeadSection, TagServices, StepsSection, IconSection, DevServices, RevealDirective],
  templateUrl: './aeo-marketing-service.html',
  styleUrl: './aeo-marketing-service.scss'
})
export class AeoMarketingService implements OnInit {
  protected file = 'aeo-marketing-service';
  /** Hero heading — "aeo" in the brand gradient, "marketing service" in white. */
  protected heroTitleHtml = '<span class="gradient-text">aeo</span> marketing service';

  // Band 1: section 0 ("answer engine optimization services") is a split header (lead-section);
  // section 1 ("chatgpt optimization") renders centered with check-pill tags; section 2
  // ("entity optimization") renders as a split header with check-pill tags.
  protected introA = AEO_SECTIONS_INTRO[0];
  protected chatgptSection = AEO_SECTIONS_INTRO[1];
  protected entitySection = AEO_SECTIONS_INTRO[2];
  // Band 2: "ai citation building services" renders as a split header + check-pill tags;
  // the rest (schema, Google AI Overviews) keep the generic centered content-section.
  protected citationSection = AEO_SECTIONS_TECH[0];
  // "schema markup & structured data optimization" — heading/intro left + gradient-label cards right.
  protected schemaSection = AEO_SECTIONS_TECH[1];
  // "google ai overviews optimization" — icon section (Google AI icon left, text right).
  protected googleAiSection = AEO_SECTIONS_TECH[2];
  // Band 3: "generative engine optimization (GEO)" — split header + AI-platform icon cards.
  protected geoSection = AEO_SECTIONS_GROWTH[0];
  protected geoCards = [
    { icon: 'chatgpt.png',         label: 'chatgpt' },
    { icon: 'perplexity.png',      label: 'perplexity' },
    { icon: 'claude.png',          label: 'claude' },
    { icon: 'gemini.png',          label: 'gemini' },
    { icon: 'google-ai-logo.png',  label: 'google ai' },
    { icon: 'future-ai.png',       label: 'future ai search platforms' },
  ];
  // "AEO reporting & AI visibility tracking" — split header + check-pill tags.
  protected reportingSection = AEO_SECTIONS_GROWTH[1];
  // "why choose semnexus as your AEO agency" — centered lead-section.
  protected whyChooseSection = AEO_SECTIONS_GROWTH[2];

  private calendly = inject(CalendlyService);
  private seo = inject(SeoService);

  ngOnInit() {
    this.seo.apply({
      title: 'Answer Engine Optimization (AEO) Services',
      description: 'Answer engine optimization services that make your brand the trusted answer across ChatGPT, Google AI Overviews, Perplexity, Claude, and Gemini — through entity optimization, AI citation building, schema, and GEO.',
      canonical: '/aeo-marketing-services/',
      image: 'https://semnexus.com/images/aeo-marketing-service/backgrounds/aeo-marketing-service.avif',
      imageAlt: 'Answer Engine Optimization services by SEM Nexus',
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'AEO Services' },
    ]);
    this.seo.setJsonLd('service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://semnexus.com/aeo-marketing-services/#service',
      name: 'Answer Engine Optimization',
      serviceType: 'AI search optimization (AEO/GEO/SGE)',
      description: 'SEO and AEO services that get brands cited in ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews through entity optimization, AI citation building, structured data, and generative engine optimization.',
      provider: { '@id': 'https://semnexus.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      url: 'https://semnexus.com/aeo-marketing-services/',
      offers: {
        '@type': 'OfferCatalog',
        name: 'Answer engine optimization services',
        itemListElement: [
          { '@type': 'Offer', name: 'ChatGPT Optimization & AI Search Visibility', description: 'Improve visibility within AI-generated answers via authority signals, entity recognition, structured content, and third-party validation.' },
          { '@type': 'Offer', name: 'Entity Optimization & Knowledge Graph Development', description: 'Knowledge graph development, entity mapping, brand disambiguation, digital footprint expansion, and AI identity verification.' },
          { '@type': 'Offer', name: 'AI Citation Building', description: 'Build trusted citations across publications, news, review platforms, directories, podcasts, forums, and thought leadership that influence AI retrieval.' },
          { '@type': 'Offer', name: 'Schema Markup & Structured Data Optimization', description: 'Organization, FAQ, Product, Service, Software, Article, Review, and knowledge graph schema to improve machine readability.' },
          { '@type': 'Offer', name: 'Google AI Overviews Optimization', description: 'Content structures, authority signals, entity relationships, and technical implementations to earn inclusion in Google AI Overviews.' },
          { '@type': 'Offer', name: 'Generative Engine Optimization (GEO)', description: 'Authority across ChatGPT, Perplexity, Claude, Gemini, Google AI, and future AI search platforms.' },
          { '@type': 'Offer', name: 'AEO Reporting & AI Visibility Tracking', description: 'Track AI citations, brand mentions, competitive visibility, answer engine presence, share of voice, entity growth, and authority development.' },
        ],
      },
    });
    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://semnexus.com/aeo-marketing-services/#faq',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  scheduleCall() {
    this.calendly.openPopup().catch(() => { });
  }
}
